import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context';
import styles from './LoginPage.module.css';

// Theme hook
function useTheme() {
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('ohcs-theme');
      if (saved === 'light' || saved === 'dark') return saved;
      return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
    }
    return 'dark';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('ohcs-theme', theme);
  }, [theme]);

  const toggleTheme = () => setTheme(prev => prev === 'dark' ? 'light' : 'dark');

  return { theme, toggleTheme };
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    mdaCode: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isPlatformLogin, setIsPlatformLogin] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);

  useEffect(() => {
    // Trigger animations after mount
    const timer = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Redirect if already authenticated
  if (isAuthenticated) {
    const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';
    navigate(from, { replace: true });
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(
        formData.email,
        formData.password,
        isPlatformLogin ? undefined : formData.mdaCode,
        isPlatformLogin
      );

      const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';
      navigate(isPlatformLogin ? '/platform' : from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/welcome');
  };

  return (
    <div className={styles.page}>
      {/* Animated Background Particles */}
      <div className={styles.particlesContainer}>
        {[...Array(20)].map((_, i) => (
          <div key={i} className={styles.particle} style={{
            '--delay': `${i * 0.5}s`,
            '--x': `${Math.random() * 100}%`,
            '--duration': `${15 + Math.random() * 20}s`,
          } as React.CSSProperties} />
        ))}
      </div>

      {/* Left Panel - Visual Showcase */}
      <div className={`${styles.leftPanel} ${loaded ? styles.leftPanelLoaded : ''}`}>
        <div className={styles.leftContent}>
          {/* Ghana Coat of Arms Inspired Visual */}
          <div className={styles.coatOfArms}>
            <div className={styles.shieldOuter}>
              <div className={styles.shieldInner}>
                <div className={styles.shieldStripes}>
                  <div className={styles.stripeGreen} />
                  <div className={styles.stripeGold}>
                    <span className={styles.blackStar}>★</span>
                  </div>
                  <div className={styles.stripeRed} />
                </div>
              </div>
            </div>
            <div className={styles.eagleLeft}>
              <svg viewBox="0 0 50 50" fill="currentColor">
                <path d="M25 5 L5 25 L15 25 L10 45 L25 30 L40 45 L35 25 L45 25 Z" />
              </svg>
            </div>
            <div className={styles.eagleRight}>
              <svg viewBox="0 0 50 50" fill="currentColor">
                <path d="M25 5 L5 25 L15 25 L10 45 L25 30 L40 45 L35 25 L45 25 Z" />
              </svg>
            </div>
          </div>

          <h1 className={styles.leftTitle}>
            <span className={styles.titleLine1}>Records</span>
            <span className={styles.titleLine2}>Management</span>
            <span className={styles.titleLine3}>System</span>
          </h1>

          <p className={styles.leftSubtitle}>
            Office of the Head of Civil Service
          </p>

          <div className={styles.leftFeatures}>
            <div className={styles.leftFeature}>
              <div className={styles.featureIcon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <path d="M9 12l2 2 4-4" />
                </svg>
              </div>
              <div>
                <span className={styles.featureTitle}>Government-Grade Security</span>
                <span className={styles.featureDesc}>AES-256 encryption at rest and in transit</span>
              </div>
            </div>
            <div className={styles.leftFeature}>
              <div className={styles.featureIcon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <div>
                <span className={styles.featureTitle}>Digital Personnel Files</span>
                <span className={styles.featureDesc}>Complete custody tracking and audit trails</span>
              </div>
            </div>
            <div className={styles.leftFeature}>
              <div className={styles.featureIcon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" />
                </svg>
              </div>
              <div>
                <span className={styles.featureTitle}>24/7 Availability</span>
                <span className={styles.featureDesc}>Offline-first PWA architecture</span>
              </div>
            </div>
          </div>

          <div className={styles.leftFooter}>
            <span>© {new Date().getFullYear()} Government of Ghana</span>
          </div>
        </div>

        {/* Animated Background Elements */}
        <div className={styles.leftBgElements}>
          <div className={styles.bgCircle1} />
          <div className={styles.bgCircle2} />
          <div className={styles.bgCircle3} />
          <div className={styles.kenteLines} />
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className={`${styles.rightPanel} ${loaded ? styles.rightPanelLoaded : ''}`}>
        {/* Top Actions */}
        <div className={styles.topActions}>
          <button onClick={handleBack} className={styles.backButton} aria-label="Go back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <button onClick={toggleTheme} className={styles.themeToggle} aria-label="Toggle theme">
            {theme === 'dark' ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="5" />
                <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
            )}
          </button>
        </div>

        <div className={styles.formContainer}>
          {/* Header */}
          <div className={styles.formHeader}>
            <div className={styles.logoIcon}>
              <svg width="56" height="56" viewBox="0 0 48 48" fill="none">
                <path
                  d="M4 14C4 11.7909 5.79086 10 8 10H18L22 14H40C42.2091 14 44 15.7909 44 18V38C44 40.2091 42.2091 42 40 42H8C5.79086 42 4 40.2091 4 38V14Z"
                  className={styles.logoFolderBase}
                />
                <rect x="12" y="20" width="24" height="4" rx="1" className={styles.logoDocLine} />
                <rect x="12" y="26" width="18" height="3" rx="1" className={styles.logoDocLine} />
                <rect x="12" y="31" width="20" height="3" rx="1" className={styles.logoDocLine} />
                <circle cx="38" cy="8" r="6" className={styles.logoStarCircle} />
                <text x="38" y="11" textAnchor="middle" className={styles.logoStarText}>★</text>
              </svg>
            </div>
            <h1 className={styles.formTitle}>Welcome Back</h1>
            <p className={styles.formSubtitle}>Sign in to access your institution's portal</p>
          </div>

          {/* Login Type Selector */}
          <div className={styles.loginTypeSelector}>
            <button
              type="button"
              className={`${styles.loginType} ${!isPlatformLogin ? styles.loginTypeActive : ''}`}
              onClick={() => setIsPlatformLogin(false)}
            >
              <div className={styles.loginTypeIcon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <path d="M9 22V12h6v10" />
                </svg>
              </div>
              <span className={styles.loginTypeName}>Institution</span>
              <span className={styles.loginTypeDesc}>MDA Portal Access</span>
            </button>
            <button
              type="button"
              className={`${styles.loginType} ${isPlatformLogin ? styles.loginTypeActive : ''}`}
              onClick={() => setIsPlatformLogin(true)}
            >
              <div className={styles.loginTypeIcon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              </div>
              <span className={styles.loginTypeName}>Platform</span>
              <span className={styles.loginTypeDesc}>Administrator Access</span>
            </button>
          </div>

          {/* Form */}
          <form className={styles.form} onSubmit={handleSubmit}>
            {error && (
              <div className={styles.error}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 8v4M12 16h.01" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            {!isPlatformLogin && (
              <div className={`${styles.inputGroup} ${focusedField === 'mda' ? styles.inputGroupFocused : ''}`}>
                <label className={styles.label}>
                  <span className={styles.labelText}>Institution Code</span>
                  <span className={styles.labelHint}>Your MDA identifier</span>
                </label>
                <div className={styles.inputWrapper}>
                  <div className={styles.inputIcon}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16m14 0H5m14 0h2m-16 0H3" />
                      <path d="M9 7h1m5 0h1M9 11h1m5 0h1M9 15h1m5 0h1" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="e.g., OHCS"
                    value={formData.mdaCode}
                    onChange={e => setFormData({ ...formData, mdaCode: e.target.value.toUpperCase() })}
                    onFocus={() => setFocusedField('mda')}
                    onBlur={() => setFocusedField(null)}
                    required
                    className={styles.input}
                  />
                </div>
              </div>
            )}

            <div className={`${styles.inputGroup} ${focusedField === 'email' ? styles.inputGroupFocused : ''}`}>
              <label className={styles.label}>
                <span className={styles.labelText}>Email Address</span>
              </label>
              <div className={styles.inputWrapper}>
                <div className={styles.inputIcon}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <path d="M22 6l-10 7L2 6" />
                  </svg>
                </div>
                <input
                  type="email"
                  placeholder="you@mda.gov.gh"
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  required
                  className={styles.input}
                />
              </div>
            </div>

            <div className={`${styles.inputGroup} ${focusedField === 'password' ? styles.inputGroupFocused : ''}`}>
              <label className={styles.label}>
                <span className={styles.labelText}>Password</span>
                <a href="#" className={styles.forgotLink}>Forgot password?</a>
              </label>
              <div className={styles.inputWrapper}>
                <div className={styles.inputIcon}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                </div>
                <input
                  type="password"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={e => setFormData({ ...formData, password: e.target.value })}
                  onFocus={() => setFocusedField('password')}
                  onBlur={() => setFocusedField(null)}
                  required
                  className={styles.input}
                />
              </div>
            </div>

            <button type="submit" disabled={isLoading} className={styles.submitButton}>
              {isLoading ? (
                <>
                  <span className={styles.spinner} />
                  <span>Authenticating...</span>
                </>
              ) : (
                <>
                  <span>Sign In to Portal</span>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </>
              )}
              <div className={styles.buttonGlow} />
            </button>
          </form>

          {/* Security Badges */}
          <div className={styles.securityBadges}>
            <div className={styles.badgeDivider}>
              <span>Protected Access</span>
            </div>
            <div className={styles.badges}>
              <span className={styles.badge}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                AES-256
              </span>
              <span className={styles.badge}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                2FA Ready
              </span>
              <span className={styles.badge}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 12l2 2 4-4" />
                  <circle cx="12" cy="12" r="10" />
                </svg>
                Certified
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
